import { ReactNode } from 'react';
import { LucideIcon } from 'lucide-react';

export interface SettingsSectionProps {
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: LucideIcon;
  /** Akcje po prawej nagłówka (np. "Reset", Toggle, Badge status). */
  actions?: ReactNode;
  /** Gęstszy wariant - mniejszy padding, sensowny dla zagnieżdżonych sekcji. */
  dense?: boolean;
  /** Ukryj outer padding zawartości (dla np. listy ToggleRow w środku). */
  flushContent?: boolean;
  children: ReactNode;
  className?: string;
}

/**
 * Karta sekcji na stronach ustawień. Wzorzec "ikona + tytuł + subtitle +
 * zawartość" powtarzający się 50+ razy. Zastępuje inline
 * `rounded-xl bg-dark-800/40 border border-white/5 p-6 + <h2> + ...`.
 *
 * Anatomia strony ustawień:
 *   <PageHeader />
 *   <SettingsSection ...>
 *     <FormField> / <ToggleRow> / ...
 *   </SettingsSection>
 *   (sticky save bar)
 */
export function SettingsSection({
  title,
  subtitle,
  icon: Icon,
  actions,
  dense = false,
  flushContent = false,
  children,
  className,
}: SettingsSectionProps) {
  const outerPad = dense ? 'p-4' : 'p-6';
  const headerGap = dense ? 'mb-4' : 'mb-7';

  return (
    <section
      className={[
        'rounded-card bg-surface-raised/40 border border-border-subtle backdrop-blur-xl',
        flushContent ? '' : outerPad,
        className,
      ].filter(Boolean).join(' ')}
    >
      <div className={[flushContent ? `${outerPad} pb-0` : '', 'flex items-center justify-between gap-3', headerGap].filter(Boolean).join(' ')}>
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {Icon && (
            <div className="shrink-0 w-11 h-11 rounded-lg bg-accent-primary/10 border border-accent-primary/20 flex items-center justify-center">
              <Icon className="w-5 h-5 text-accent-primary" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold text-text-primary leading-tight">{title}</h2>
            {subtitle && (
              <p className="mt-1 text-sm text-text-muted leading-relaxed">{subtitle}</p>
            )}
          </div>
        </div>
        {actions && <div className="shrink-0 flex items-center gap-2">{actions}</div>}
      </div>
      <div>{children}</div>
    </section>
  );
}
